import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Brain, User as UserIcon, Image as ImageIcon, X, ChevronDown, History, Plus } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../../lib/api";

const QUICK_PROMPTS = [
  "Analizza la situazione del business attuale e suggerisci 5 mosse strategiche per crescere il fatturato del 30%",
  "Scrivi una mail professionale per recuperare un credito scaduto da 15 giorni",
  "Calcola la liquidazione IVA del trimestre attuale e dimmi cosa devo versare con F24",
  "Genera un piano marketing weekend per attirare clienti turisti",
  "Sto pensando di acquistare 3 auto in leasing — convieni con i numeri attuali?",
  "Quali sono le scadenze fiscali, assicurative e di manutenzione dei prossimi 60 giorni?",
];

const newSessionId = () => `oracle-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function Oracle() {
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState({});
  const [modelKey, setModelKey] = useState("claude-sonnet-4-5");
  const [showModels, setShowModels] = useState(false);
  const [imageData, setImageData] = useState(null);   // base64 data URL
  const [imageName, setImageName] = useState("");
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    api.get("/ai/oracle/models").then((r) => {
      setModels(r.data.models || {});
      setModelKey(r.data.default || "claude-sonnet-4-5");
    }).catch(() => {});
    loadSessions();
  }, []);

  const loadSessions = () => {
    api.get("/ai/oracle/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
  };

  const loadSession = async (sid) => {
    const r = await api.get(`/ai/history/${sid}`);
    setSessionId(sid);
    setMessages((r.data || []).map((m) => ({ role: m.role, content: m.content })));
    setShowSessions(false);
  };

  const newSession = () => {
    setSessionId(newSessionId());
    setMessages([]);
    setShowSessions(false);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { alert("Max 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setImageData(reader.result); setImageName(f.name); };
    reader.readAsDataURL(f);
  };

  const send = async (text) => {
    const message = text || input.trim();
    if (!message && !imageData) return;
    const userMsg = { role: "user", content: message, image: imageData };
    setMessages((m) => [...m, userMsg]);
    setInput(""); setLoading(true);
    const sentImage = imageData;
    setImageData(null); setImageName("");
    try {
      const r = await api.post("/ai/oracle", {
        session_id: sessionId,
        message,
        model: modelKey,
        image_base64: sentImage || undefined,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.response, model: r.data.label }]);
      loadSessions();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Errore: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setLoading(false); }
  };

  const currentModel = models[modelKey] || { label: "—", capabilities: "" };

  return (
    <div className="h-[calc(100vh-9rem)] flex flex-col" data-testid="oracle-page">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Brain className="text-violet-400" size={32} />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-[#ff0033] bg-clip-text text-transparent">FabriOracle</span>
            <span className="text-xs uppercase tracking-[0.3em] text-white/40 font-display">Super AI</span>
          </h1>
          <p className="text-sm text-white/45 mt-1">
            Il cervello onnisciente del gestionale · Multi-modello · Visione · Memoria · Contesto live
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Model selector */}
          <div className="relative">
            <button onClick={() => setShowModels(!showModels)} data-testid="oracle-model-selector"
              className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25">
              <Sparkles size={12} /> {currentModel.label} <ChevronDown size={12} />
            </button>
            {showModels && (
              <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-auto rounded-2xl glass-strong border border-white/10 p-2 z-30">
                {Object.entries(models).map(([key, m]) => (
                  <button key={key} onClick={() => { setModelKey(key); setShowModels(false); }}
                    data-testid={`oracle-model-${key}`}
                    className={`w-full text-left px-3 py-2 rounded-xl hover:bg-white/[0.05] ${modelKey === key ? 'bg-violet-500/15' : ''}`}>
                    <div className="text-sm font-medium">{m.label}</div>
                    <div className="text-[10px] text-white/45 uppercase tracking-widest mt-0.5">{m.provider} · {m.capabilities}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Sessions */}
          <div className="relative">
            <button onClick={() => setShowSessions(!showSessions)} title="Cronologia sessioni"
              className="p-2 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08]">
              <History size={14} />
            </button>
            {showSessions && (
              <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-auto rounded-2xl glass-strong border border-white/10 p-2 z-30">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-between">
                  <span>Sessioni recenti</span>
                  <button onClick={newSession} className="text-violet-300 hover:text-violet-200 inline-flex items-center gap-1"><Plus size={10} /> Nuova</button>
                </div>
                {sessions.length === 0 && <div className="px-3 py-4 text-xs text-white/40">Nessuna sessione</div>}
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                    className={`w-full text-left px-3 py-2 rounded-xl hover:bg-white/[0.05] ${s.session_id === sessionId ? 'bg-violet-500/10' : ''}`}>
                    <div className="text-xs truncate">{s.preview || '(vuota)'}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{s.count} msg · {s.last_ts?.slice(0,16).replace('T',' ')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={newSession} title="Nuova chat" data-testid="oracle-new-session"
            className="p-2 rounded-full bg-violet-500/20 border border-violet-500/40 hover:bg-violet-500/30">
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 glass rounded-2xl overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto p-6 space-y-4" data-testid="oracle-messages">
          {messages.length === 0 && (
            <div className="h-full grid place-items-center">
              <div className="text-center max-w-3xl">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-violet-500/20 via-fuchsia-500/15 to-[#ff0033]/15 border border-violet-400/40 grid place-items-center">
                  <Brain size={36} className="text-violet-300" />
                </div>
                <div className="font-display text-3xl font-bold mt-5 bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  Sono FabriOracle
                </div>
                <p className="text-sm text-white/55 mt-3 leading-relaxed">
                  Sono l'AI più informata e intelligente del tuo gestionale. Posso vedere tutti i tuoi dati live,
                  analizzare immagini e documenti, scrivere mail, generare contratti, calcolare tasse,
                  fare pianificazione strategica e rispondere a qualsiasi domanda.
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button key={p} onClick={() => send(p)}
                      data-testid={`oracle-quick-${p.slice(0,20)}`}
                      className="text-xs px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-violet-400/40 hover:bg-violet-500/5 transition text-left">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-400/40 grid place-items-center">
                  <Brain size={16} className="text-violet-300" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-violet-500/15 border border-violet-500/40 text-white" : "bg-white/[0.03] border border-white/10"
              }`}>
                {m.image && (
                  <img src={m.image} alt="upload" className="max-w-xs rounded-lg mb-2 border border-white/10" />
                )}
                {m.role === "assistant" ? (
                  <div className="prose-oracle">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  <span className="whitespace-pre-wrap">{m.content}</span>
                )}
                {m.model && <div className="text-[9px] text-white/30 uppercase tracking-widest mt-2">{m.model}</div>}
              </div>
              {m.role === "user" && (
                <div className="w-9 h-9 shrink-0 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center">
                  <UserIcon size={16} className="text-white/70" />
                </div>
              )}
            </motion.div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-400/40 grid place-items-center">
                <Brain size={16} className="text-violet-300 animate-pulse" />
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3 flex gap-1">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full bg-violet-400"
                    style={{ animation: `pulse-neon 1s ${i * 0.15}s ease-in-out infinite` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {imageData && (
          <div className="px-4 pt-3 border-t border-white/5 flex items-center gap-3">
            <img src={imageData} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
            <div className="flex-1">
              <div className="text-xs">{imageName}</div>
              <div className="text-[10px] text-white/45">L'immagine sarà inviata all'AI</div>
            </div>
            <button onClick={() => { setImageData(null); setImageName(""); }} className="p-1.5 rounded-lg bg-white/[0.05] hover:bg-rose-500/20"><X size={14} /></button>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/5 p-4 flex gap-2 items-end">
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden ref={fileRef} onChange={handleFile} data-testid="oracle-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} title="Allega immagine"
            data-testid="oracle-attach-image"
            className="p-2.5 rounded-full bg-white/[0.04] border border-white/10 hover:bg-violet-500/15 hover:border-violet-500/40">
            <ImageIcon size={14} />
          </button>
          <textarea data-testid="oracle-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Chiedi qualsiasi cosa a FabriOracle…"
            rows={1}
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-3 text-sm focus:border-violet-400/60 focus:outline-none resize-none max-h-32" />
          <button data-testid="oracle-send" disabled={loading || (!input.trim() && !imageData)} type="submit"
            className="px-5 py-3 rounded-full inline-flex items-center gap-2 text-sm font-bold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-40">
            <Send size={14} /> Invia
          </button>
        </form>
      </div>
    </div>
  );
}
